import * as tslib_1 from "tslib";
import addDays from 'date-fns/add_days';
import addSeconds from 'date-fns/add_seconds';
import startOfDay from 'date-fns/start_of_day';
import startOfMonth from 'date-fns/start_of_month';
import startOfWeek from 'date-fns/start_of_week';
import startOfYear from 'date-fns/start_of_year';
import subDays from 'date-fns/sub_days';
import subMonths from 'date-fns/sub_months';
import subWeeks from 'date-fns/sub_weeks';
import subYears from 'date-fns/sub_years';
import { TimeHeaderId } from 'owa-mail-group-headers';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import strings from 'owa-strings/owa-common-strings';
import mailStrings from 'owa-strings/owa-mail-strings';
/**
 * Generate time group headers
 * @param time Time specified in user time zone based on which to generate time ranges
 * @return An array of time group ranges for mail list headers
 */
export default function generateTimeGroupHeaders(time, weekStartDay) {
    var monthNames = [
        strings.januaryUppercase,
        strings.februaryUppercase,
        strings.marchUppercase,
        strings.aprilUppercase,
        strings.mayUppercase,
        strings.juneUppercase,
        strings.julyUppercase,
        strings.augustUppercase,
        strings.septemberUppercase,
        strings.octoberUppercase,
        strings.novemberUppercase,
        strings.decemberUppercase,
    ];
    // References in time that are used to build time group headers.
    var dateTimeReferences = {
        adjustedTime: time,
        currentYear: time.getFullYear(),
        lastWeek: startOfWeek(subWeeks(time, 1), {
            weekStartsOn: weekStartDay,
        }),
        oneMonthAgo: startOfMonth(subMonths(time, 1)),
        pinnedItemTimestamp: new Date(Date.UTC(4500, 8, 1)) /* Sept 1 4500 */,
        startofThisMonth: startOfMonth(time),
        startOfThisWeek: startOfWeek(time, {
            weekStartsOn: weekStartDay,
        }),
        todayStartTime: startOfDay(time),
        tomorrowStartTime: startOfDay(addDays(time, 1)),
        twoMonthsAgo: startOfMonth(subMonths(time, 2)),
        yesterdayStartTime: startOfDay(subDays(time, 1)),
    };
    var consumerHeaders = [
        0 /* Pinned */,
        1 /* Today */,
        2 /* Yesterday */,
        5 /* ThisMonth */,
        10 /* OneMonthOlder */,
        11 /* TwoMonthsOlder */,
        9 /* Older */,
    ];
    var enterpriseHeaders = [
        0 /* Pinned */,
        1 /* Today */,
        2 /* Yesterday */,
        3 /* ThisWeek */,
        4 /* LastWeek */,
        5 /* ThisMonth */,
        6 /* LastMonth */,
        7 /* SpecificMonth */,
        8 /* SpecificYear */,
    ];
    // Determine header types depending on the product.
    var headerTypes = isConsumer() ? consumerHeaders : enterpriseHeaders;
    // Generate headers from header types.
    return generateTimeGroupHeadersFromTimeGroupHeaderTypes(dateTimeReferences, headerTypes, monthNames);
}
/**
 * Generates headers from header types.
 * @param dateTimeReferences Object with references required to build TimeGroupHeader objects
 * @param headerTypes Determines which headers are returned
 * @param monthNames Array of month name strings
 * @returns An array of header objects
 */
function generateTimeGroupHeadersFromTimeGroupHeaderTypes(dateTimeReferences, headerTypes, monthNames) {
    var e_1, _a;
    var timeGroupHeaders = [];
    try {
        for (var headerTypes_1 = tslib_1.__values(headerTypes), headerTypes_1_1 = headerTypes_1.next(); !headerTypes_1_1.done; headerTypes_1_1 = headerTypes_1.next()) {
            var headerType = headerTypes_1_1.value;
            var timeGroupHeadersFromHeaderType = mapHeaderTypeToTimeGroupHeaders(dateTimeReferences, headerType, monthNames);
            timeGroupHeaders.push.apply(timeGroupHeaders, tslib_1.__spread(timeGroupHeadersFromHeaderType));
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (headerTypes_1_1 && !headerTypes_1_1.done && (_a = headerTypes_1.return)) _a.call(headerTypes_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return timeGroupHeaders;
}
/**
 * Maps header type to header objects.
 * @param dateTimeReferences Object with references required to build TimeGroupHeader objects
 * @param headerType Determines which headers are returned
 * @param monthNames Array of month name strings
 * @returns An array of header objects
 */
function mapHeaderTypeToTimeGroupHeaders(dateTimeReferences, headerType, monthNames) {
    switch (headerType) {
        // "Pinned" header.
        case 0 /* Pinned */:
            return [
                {
                    headerText: function () { return mailStrings.pinnedGroupHeader; },
                    headerId: TimeHeaderId.Pinned,
                    rangeStartTime: dateTimeReferences.pinnedItemTimestamp,
                    rangeEndTime: addSeconds(dateTimeReferences.pinnedItemTimestamp, 1) /* add one second */,
                },
            ];
        // "Today" header.
        case 1 /* Today */:
            return [
                {
                    headerText: function () { return mailStrings.todayGroupHeader; },
                    headerId: TimeHeaderId.Today,
                    rangeStartTime: dateTimeReferences.todayStartTime,
                    rangeEndTime: dateTimeReferences.tomorrowStartTime,
                },
            ];
        // "Yesterday" header.
        case 2 /* Yesterday */:
            return [
                {
                    headerText: function () { return mailStrings.yesterdayGroupHeader; },
                    headerId: TimeHeaderId.Yesterday,
                    rangeStartTime: dateTimeReferences.yesterdayStartTime,
                    rangeEndTime: dateTimeReferences.todayStartTime,
                },
            ];
        // "This week" header.
        case 3 /* ThisWeek */:
            return [
                {
                    headerText: function () { return mailStrings.thisWeekGroupHeader; },
                    headerId: TimeHeaderId.ThisWeek,
                    rangeStartTime: dateTimeReferences.startOfThisWeek,
                    rangeEndTime: dateTimeReferences.yesterdayStartTime,
                },
            ];
        // "Last week" header.
        case 4 /* LastWeek */:
            return [
                {
                    headerText: function () { return mailStrings.lastWeekGroupHeader; },
                    headerId: TimeHeaderId.LastWeek,
                    rangeStartTime: dateTimeReferences.lastWeek,
                    rangeEndTime: dateTimeReferences.startOfThisWeek,
                },
            ];
        // "This month" header.
        case 5 /* ThisMonth */:
            if (dateTimeReferences.startofThisMonth < dateTimeReferences.yesterdayStartTime) {
                return [
                    {
                        headerText: function () { return mailStrings.thisMonthGroupHeader; },
                        headerId: TimeHeaderId.ThisMonth,
                        rangeStartTime: dateTimeReferences.startofThisMonth,
                        rangeEndTime: dateTimeReferences.yesterdayStartTime,
                    },
                ];
            }
            return [];
        // "Last month" header.
        case 6 /* LastMonth */:
            return [
                {
                    headerText: function () { return mailStrings.lastMonthGroupHeader; },
                    headerId: TimeHeaderId.LastMonth,
                    rangeStartTime: dateTimeReferences.oneMonthAgo,
                    rangeEndTime: dateTimeReferences.startofThisMonth,
                },
            ];
        // Haders for specific months of the year, up until the previous year.
        case 7 /* SpecificMonth */:
            /**
             * Add headers for preceding months of the current year (not including the
             * previous month, as that is handled by "Last month" header).
             */
            var monthHeaders = [];
            var monthIterator = 2;
            var monthHeaderStartTime = startOfMonth(subMonths(dateTimeReferences.adjustedTime, monthIterator));
            var monthHeaderEndTime = startOfMonth(subMonths(dateTimeReferences.adjustedTime, monthIterator - 1));
            var _loop_1 = function () {
                var monthName = monthNames[monthHeaderStartTime.getMonth()];
                monthHeaders.push({
                    headerText: function () { return monthName; },
                    headerId: monthName,
                    rangeStartTime: monthHeaderStartTime,
                    rangeEndTime: monthHeaderEndTime,
                });
                monthIterator++;
                monthHeaderStartTime = startOfMonth(subMonths(dateTimeReferences.adjustedTime, monthIterator));
                monthHeaderEndTime = startOfMonth(subMonths(dateTimeReferences.adjustedTime, monthIterator - 1));
            };
            while (monthHeaderStartTime.getFullYear() === dateTimeReferences.currentYear) {
                _loop_1();
            }
            return monthHeaders;
        // Headers for specific years (before current year and until 1970).
        case 8 /* SpecificYear */:
            /**
             * Add headers for preceding years until 1970 (not including the current
             * year, as that is handled by the month headers)
             */
            var yearHeaders = [];
            var yearIterator = 1;
            var yearStartTime = startOfYear(subYears(dateTimeReferences.adjustedTime, yearIterator));
            var yearEndTime = startOfYear(subYears(dateTimeReferences.adjustedTime, yearIterator - 1));
            var earliestYear = new Date(0).getFullYear() /* 1970 */;
            var _loop_2 = function () {
                var year = yearStartTime.getFullYear();
                yearHeaders.push({
                    headerText: function () { return year.toString(); },
                    headerId: year,
                    rangeStartTime: yearStartTime,
                    rangeEndTime: yearEndTime,
                });
                yearIterator++;
                yearStartTime = startOfYear(subYears(dateTimeReferences.adjustedTime, yearIterator));
                yearEndTime = startOfYear(subYears(dateTimeReferences.adjustedTime, yearIterator - 1));
            };
            while (yearStartTime.getFullYear() >= earliestYear) {
                _loop_2();
            }
            return yearHeaders;
        // "Older" header.
        case 9 /* Older */:
            return [
                {
                    headerText: function () { return mailStrings.olderGroupHeader; },
                    headerId: TimeHeaderId.Older,
                    rangeStartTime: new Date(0) /* Jan 1st 1970 00:00:00 */,
                    rangeEndTime: dateTimeReferences.twoMonthsAgo,
                },
            ];
        // Header for one month ago.
        case 10 /* OneMonthOlder */:
            return [
                {
                    headerText: function () { return monthNames[dateTimeReferences.oneMonthAgo.getMonth()]; },
                    headerId: TimeHeaderId.OneMonthOlder,
                    rangeStartTime: dateTimeReferences.oneMonthAgo,
                    rangeEndTime: dateTimeReferences.startofThisMonth,
                },
            ];
        // Header for two months ago.
        case 11 /* TwoMonthsOlder */:
            return [
                {
                    headerText: function () { return monthNames[dateTimeReferences.twoMonthsAgo.getMonth()]; },
                    headerId: TimeHeaderId.TwoMonthsOlder,
                    rangeStartTime: dateTimeReferences.twoMonthsAgo,
                    rangeEndTime: dateTimeReferences.oneMonthAgo,
                },
            ];
        default:
            throw new Error('Invalid TimeGroupHeaderType passed to mapHeaderTypeToTimeGroupHeaders');
    }
}
//# sourceMappingURL=generateTimeGroupHeaders.js.map